import type { BackendApi } from '../backend/client'

/**
 * Creates a Plaid Link token: for a new HSA connection when `itemId` is
 * omitted, or to reconnect an existing item when it's given. `hosted` requests
 * a Hosted Link URL for the Expo in-app browser instead of an embedded token --
 * see lib/plaid/hostedLink.ts, which stays a web/native-shell concern and is
 * not part of this command surface.
 */
export function createLinkToken(
  api: BackendApi,
  params: { membershipId: string; itemId?: string; hosted?: boolean },
) {
  return api.createPlaidLinkToken({
    membership_id: params.membershipId,
    item_id: params.itemId,
    hosted: params.hosted,
  })
}

export function exchangePublicToken(
  api: BackendApi,
  params: {
    publicToken: string
    membershipId: string
    institutionId?: string | null
    institutionName?: string | null
  },
) {
  return api.exchangePlaidPublicToken({
    public_token: params.publicToken,
    membership_id: params.membershipId,
    institution_id: params.institutionId,
    institution_name: params.institutionName,
  })
}

/** Finishes an in-app (Expo) Hosted Link session; polled until it stops 409-ing. */
export function exchangeHostedLink(
  api: BackendApi,
  params: { linkToken: string; membershipId: string; itemId?: string },
) {
  return api.exchangePlaidHostedLink({
    link_token: params.linkToken,
    membership_id: params.membershipId,
    item_id: params.itemId,
  })
}

/** Lists the caller's connected Plaid items and accounts for a membership. */
export function listItems(api: BackendApi, membershipId: string) {
  return api.listPlaidItems({ membership_id: membershipId })
}

export function disconnectItem(api: BackendApi, itemId: string) {
  return api.disconnectPlaidItem(itemId)
}

/** Triggers a manual transaction sync for a Plaid item (the settings-page "Sync now" button). */
export function syncItem(api: BackendApi, itemId: string) {
  return api.syncPlaidItem(itemId)
}

/** Clears an item's requires_reauth state after a successful update-mode Link session. */
export function reconnectItem(api: BackendApi, itemId: string) {
  return api.reconnectPlaidItem(itemId)
}
